import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Check, Dumbbell, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import type { WordSense } from "@preptrack/contracts";

import { Button, EmptyState, Field, LoadingLine, PageHeader, Tag, Textarea } from "@/components/primitives";
import { apiFetch } from "@/lib/api";
import { BulkDeleteProgressError, deleteInSequentialBatches } from "@/lib/bulk-delete";
import { WordSenses } from "@/components/word-senses";
import { WordPronunciation } from "@/components/word-pronunciation";
import { completedSenseDrafts, senseDraftsFromSenses, SenseRowsEditor, type SenseDraft } from "@/components/sense-rows-editor";

interface PersonalWord {
  id: string;
  wordId: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  meaning: string | null;
  senses: WordSense[];
  notes: string | null;
  sourceSentence: string | null;
  createdAt: string;
  memberships: Array<{ code: string; label: string; kind: string }>;
  study: {
    learningCount: number;
    masteryStage: "unlearned" | "introduced" | "spelling" | "reinforcing" | "stable";
    masteryLabel: string;
    retrievability: number | null;
    due: boolean;
    dueOn: string | null;
  };
}

interface EditDraft {
  senses: SenseDraft[];
  notes: string;
  sourceSentence: string;
}

type ContentFilter = "all" | "with-source" | "with-notes" | "without-meaning";
type SortOrder = "recent" | "alphabetical";

const EMPTY_WORDS: PersonalWord[] = [];
const dateFormatter = new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric" });

export function WordLibraryPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["user-words"],
    queryFn: () => apiFetch<{ words: PersonalWord[] }>("/user-words"),
  });
  const words = query.data?.words ?? EMPTY_WORDS;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ContentFilter>("all");
  const [sort, setSort] = useState<SortOrder>("recent");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft>({ senses: [], notes: "", sourceSentence: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const view = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered: PersonalWord[] = [];

    for (const word of words) {
      const matchesSearch = !normalizedSearch
        || word.headword.toLowerCase().includes(normalizedSearch)
        || word.meaning?.toLowerCase().includes(normalizedSearch)
        || word.notes?.toLowerCase().includes(normalizedSearch);
      const matchesFilter = filter === "all"
        || (filter === "with-source" && Boolean(word.sourceSentence))
        || (filter === "with-notes" && Boolean(word.notes))
        || (filter === "without-meaning" && !word.meaning);
      if (matchesSearch && matchesFilter) filtered.push(word);
    }

    filtered.sort((left, right) => sort === "alphabetical"
      ? left.headword.localeCompare(right.headword, "en")
      : new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

    return { filtered };
  }, [filter, search, sort, words]);

  const allVisibleSelected = view.filtered.length > 0
    && view.filtered.every((word) => selectedIds.has(word.id));
  const studiedCount = words.reduce((total, word) => total + (word.study.learningCount > 0 ? 1 : 0), 0);
  const stableCount = words.reduce((total, word) => total + (word.study.masteryStage === "stable" ? 1 : 0), 0);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["user-words"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      queryClient.invalidateQueries({ queryKey: ["word-books"] }),
    ]);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const word of view.filtered) {
        if (allVisibleSelected) next.delete(word.id);
        else next.add(word.id);
      }
      return next;
    });
  };

  const startEditing = (word: PersonalWord) => {
    setEditingId(word.id);
    setDraft({
      senses: senseDraftsFromSenses(word.senses),
      notes: word.notes ?? "",
      sourceSentence: word.sourceSentence ?? "",
    });
    setStatus("");
    setError("");
  };

  const saveEdit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingId) return;
    setBusy(true);
    setError("");
    try {
      await apiFetch(`/user-words/${editingId}`, {
        method: "PATCH",
        body: JSON.stringify({
          senses: completedSenseDrafts(draft.senses),
          notes: draft.notes.trim() || null,
          sourceSentence: draft.sourceSentence.trim() || null,
        }),
      });
      await refresh();
      setEditingId(null);
      setStatus("词条内容已更新。");
    } catch {
      setError("词条更新失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (word: PersonalWord) => {
    if (!window.confirm(`确定从阅读词库中删除“${word.headword}”吗？复习历史不会被删除。`)) return;
    setBusy(true);
    setError("");
    try {
      await apiFetch(`/user-words/${word.id}`, { method: "DELETE" });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(word.id);
        return next;
      });
      if (editingId === word.id) setEditingId(null);
      await refresh();
      setStatus(`“${word.headword}”已从阅读词库移除。`);
    } catch {
      setError("删除失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`确定删除选中的 ${selectedIds.size} 个阅读生词吗？复习历史不会被删除。`)) return;
    setBusy(true);
    setError("");
    try {
      const result = await deleteInSequentialBatches(Array.from(selectedIds), (ids) => apiFetch("/user-words/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }));
      if (editingId && selectedIds.has(editingId)) setEditingId(null);
      setSelectedIds(new Set());
      await refresh();
      setStatus(`已移除 ${result.deletedCount} 个阅读生词。`);
    } catch (caught) {
      const deletedCount = caught instanceof BulkDeleteProgressError ? caught.progress.deletedCount : 0;
      setError(deletedCount > 0
        ? `已移除 ${deletedCount} 个阅读生词，后续批次失败，列表已重新同步。`
        : "批量删除失败，列表已重新同步。");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page word-library-page">
      <PageHeader
        title="阅读词库"
        description="集中整理你手动录入或从试卷识别保存的生词。"
        action={(
          <div className="word-library-header-actions">
            <Link className="button button--secondary button--md" to="/app/$subject/my-words" params={{ subject: "english" }}><Plus /> 录入生词</Link>
            <Link className="button button--secondary button--md" to="/app/$subject/practice" params={{ subject: "english" }}><Dumbbell /> 自由练习</Link>
            <Link className="button button--primary button--md" to="/app/$subject/review" params={{ subject: "english" }}><BookOpen /> 开始背诵</Link>
          </div>
        )}
      />

      <section className="word-library-metrics" aria-label="阅读词库统计">
        <div><strong>{words.length}</strong><span>全部生词</span></div>
        <div><strong>{studiedCount}</strong><span>已经学习</span></div>
        <div><strong>{stableCount}</strong><span>达到稳固</span></div>
      </section>

      <section className="word-library-tools" aria-label="筛选阅读生词">
        <label className="word-library-search">
          <Search />
          <span className="sr-only">搜索阅读词库</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索单词、词义或笔记" />
          {search ? <button type="button" onClick={() => setSearch("")} aria-label="清除搜索"><X /></button> : null}
        </label>
        <label>
          <span className="sr-only">内容筛选</span>
          <select value={filter} onChange={(event) => setFilter(event.target.value as ContentFilter)}>
            <option value="all">全部内容</option>
            <option value="with-source">有来源句子</option>
            <option value="with-notes">有个人笔记</option>
            <option value="without-meaning">待补充词义</option>
          </select>
        </label>
        <label>
          <span className="sr-only">排序方式</span>
          <select value={sort} onChange={(event) => setSort(event.target.value as SortOrder)}>
            <option value="recent">最近添加</option>
            <option value="alphabetical">字母顺序</option>
          </select>
        </label>
      </section>

      <div className={selectedIds.size ? "word-library-bulk word-library-bulk--visible" : "word-library-bulk"} aria-hidden={!selectedIds.size}>
        <span><Check /> 已选择 {selectedIds.size} 个词</span>
        <Button type="button" variant="danger" size="sm" onClick={() => void removeSelected()} disabled={busy || selectedIds.size === 0}><Trash2 /> 批量删除</Button>
      </div>

      {status ? <p className="save-message word-library-status" role="status">{status}</p> : null}
      {error ? <p className="error-strip" role="alert">{error}</p> : null}
      {query.isLoading ? <LoadingLine /> : null}
      {query.isError ? <p className="error-strip" role="alert">阅读词库加载失败，请刷新后重试。</p> : null}

      {!query.isLoading && words.length === 0 ? (
        <EmptyState
          title="阅读词库还是空的"
          description="录入阅读时遇到的生词，或从试卷照片中识别并确认保存。"
          action={<Link className="button button--primary button--md" to="/app/$subject/my-words" params={{ subject: "english" }}>录入第一个生词 <ArrowRight /></Link>}
        />
      ) : null}

      {!query.isLoading && words.length > 0 && view.filtered.length === 0 ? (
        <EmptyState title="没有符合条件的词" description="调整搜索内容或筛选条件后再试。" />
      ) : null}

      {view.filtered.length > 0 ? (
        <section className="word-library-list" aria-label="阅读生词列表">
          <div className="word-library-list__head">
            <label><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} /><span className="sr-only">选择当前结果</span></label>
            <span>单词</span>
            <span>词义与来源</span>
            <span>归属与掌握</span>
            <span>操作</span>
          </div>
          {view.filtered.map((word) => (
            <article className={editingId === word.id ? "word-library-row word-library-row--editing" : "word-library-row"} key={word.id}>
              <label className="word-library-row__select"><input type="checkbox" checked={selectedIds.has(word.id)} onChange={() => toggleSelected(word.id)} /><span className="sr-only">选择 {word.headword}</span></label>
              <div className="word-library-row__word">
                <Link to="/app/$subject/words/$word" params={{ subject: "english", word: word.wordId }}>{word.headword}</Link>
                <WordPronunciation wordId={word.wordId} headword={word.headword} phoneticUk={word.phoneticUk} phoneticUs={word.phoneticUs} compact />
              </div>
              <div className="word-library-row__content">
                <WordSenses senses={word.senses} variant="compact" empty="待补充词义" />
                {word.sourceSentence ? <blockquote>{word.sourceSentence}</blockquote> : <small>未记录来源句子</small>}
                {word.notes ? <small className="word-library-row__note">笔记：{word.notes}</small> : null}
              </div>
              <div className="word-library-row__meta">
                <div className="tag-list">{word.memberships.length ? word.memberships.map((membership) => <Tag key={membership.code} variant="metadata" tone={membership.kind === "official" ? "accent" : "neutral"}>{membership.label}</Tag>) : <Tag variant="metadata">个人词条</Tag>}</div>
                <div className="word-study-meta">
                  <Tag tone={word.study.masteryStage === "stable" ? "success" : word.study.masteryStage === "unlearned" ? "neutral" : "accent"}>{word.study.masteryLabel}</Tag>
                  {word.study.masteryStage === "introduced" || word.study.masteryStage === "spelling" ? <Tag variant="metadata" tone="accent">待继续</Tag> : word.study.due ? <Tag variant="metadata" tone="accent">今日到期</Tag> : null}
                  <span>学习 {word.study.learningCount} 次 · 记忆率 {word.study.retrievability === null ? "--" : `${word.study.retrievability}%`}</span>
                </div>
                <time dateTime={word.createdAt}>{dateFormatter.format(new Date(word.createdAt))} 添加</time>
              </div>
              <div className="word-library-row__actions">
                <button type="button" onClick={() => startEditing(word)} aria-label={`编辑 ${word.headword}`} title="编辑词条"><Pencil /></button>
                <button type="button" className="word-library-row__delete" onClick={() => void removeOne(word)} aria-label={`删除 ${word.headword}`} title="删除词条" disabled={busy}><Trash2 /></button>
              </div>

              {editingId === word.id ? (
                <form className="word-library-editor" onSubmit={saveEdit}>
                  <Field label="词义" htmlFor={`library-senses-${word.id}-definition-0`} optional hint="每行对应一个词性；同一词性的多个释义用分号分隔。">
                    <SenseRowsEditor value={draft.senses} onChange={(senses) => setDraft((current) => ({ ...current, senses }))} idPrefix={`library-senses-${word.id}`} />
                  </Field>
                  <Field label="个人笔记" htmlFor={`library-notes-${word.id}`} optional>
                    <Textarea id={`library-notes-${word.id}`} value={draft.notes} onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))} maxLength={1000} rows={3} />
                  </Field>
                  <div className="word-library-editor__sentence">
                    <Field label="来源句子" htmlFor={`library-sentence-${word.id}`} optional>
                      <Textarea id={`library-sentence-${word.id}`} value={draft.sourceSentence} onChange={(event) => setDraft((current) => ({ ...current, sourceSentence: event.target.value }))} maxLength={2000} rows={3} />
                    </Field>
                  </div>
                  <div className="word-library-editor__actions">
                    <Button type="button" variant="ghost" onClick={() => setEditingId(null)} disabled={busy}>取消</Button>
                    <Button type="submit" variant="primary" disabled={busy}>{busy ? "正在保存…" : "保存修改"} <Check /></Button>
                  </div>
                </form>
              ) : null}
            </article>
          ))}
        </section>
      ) : null}
    </div>
  );
}
